import { useState } from 'react'
import { cn } from '@/utils/cn'
import { focusRing } from '@/components/ui/buttonStyles'

interface TagInputProps {
  id: string
  label: string
  value: string[]
  onChange: (tags: string[]) => void
  /** Existing tags offered as the user types; ones already chosen are left out. */
  suggestions?: string[]
  maxLength?: number
}

function normalise(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLowerCase()
}

/**
 * Free-form tags as removable chips. Enter or comma commits the typed tag, as does
 * leaving the field, so a half-typed tag is not lost on submit. Backspace in the empty
 * field removes the last chip. Suggestions use a native datalist.
 */
export function TagInput({ id, label, value, onChange, suggestions = [], maxLength }: TagInputProps) {
  const [text, setText] = useState('')
  const listId = `${id}-suggestions`

  function commit() {
    const tag = normalise(text)
    setText('')
    if (tag && !value.includes(tag)) onChange([...value, tag])
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Backspace' && text === '' && value.length > 0) {
      onChange(value.slice(0, -1))
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs uppercase tracking-widest text-label font-light">
        {label}
      </label>
      <div className="flex flex-wrap items-center gap-2 min-h-11 rounded-md border border-border-divider bg-surface-bg px-3 py-2 focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/30 transition-colors duration-200">
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 rounded-full bg-btn-quiet-bg pl-3 text-sm font-light text-body"
          >
            {tag}
            <button
              type="button"
              aria-label={`Remove ${tag}`}
              onClick={() => onChange(value.filter((t) => t !== tag))}
              className={cn(
                'inline-flex items-center justify-center w-7 h-7 rounded-full text-muted hover:text-body',
                focusRing,
              )}
            >
              <span aria-hidden="true">×</span>
            </button>
          </span>
        ))}
        <input
          id={id}
          list={listId}
          maxLength={maxLength}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commit}
          placeholder={value.length === 0 ? 'Add a theme…' : undefined}
          className="flex-1 min-w-32 border-0 bg-transparent px-1 py-1 text-base sm:text-sm text-body font-light placeholder:text-label/60 focus:outline-none focus:ring-0 [&::-webkit-calendar-picker-indicator]:hidden!"
        />
        <datalist id={listId}>
          {suggestions
            .filter((s) => !value.includes(s))
            .map((s) => (
              <option key={s} value={s} />
            ))}
        </datalist>
      </div>
    </div>
  )
}
